import { FC, useState } from 'react';
import type { ApontamentoReuniao } from '@/types/dashboards';
import { fmtData, fmtDataHora, fmtUn } from '@/utils/painelFormat';

interface Props {
  apontamentos: ApontamentoReuniao[];
  /** Sem os dois, a lista é só leitura (Visão geral e o PDF da reunião). */
  onEditar?: (a: ApontamentoReuniao) => void;
  onApagar?: (a: ApontamentoReuniao) => Promise<void>;
  testId?: string;
}

const TH = 'px-2 py-2 print:px-1 text-left font-medium';

interface AcoesLinha {
  onEditar: (a: ApontamentoReuniao) => void;
  onApagar: (a: ApontamentoReuniao) => Promise<void>;
}

/** Apagar pede confirmação na própria linha (nada de `confirm()` do navegador). */
const Acoes: FC<AcoesLinha & { a: ApontamentoReuniao }> = ({ a, onEditar, onApagar }) => {
  const [confirmando, setConfirmando] = useState(false);
  const [apagando, setApagando] = useState(false);

  if (!a.pode_editar) {
    return <span className="text-xs text-content-muted" title="Só quem registrou (ou um admin) muda">—</span>;
  }
  if (confirmando) {
    const apagar = async () => {
      setApagando(true);
      try {
        await onApagar(a);
      } finally {
        setApagando(false);
        setConfirmando(false);
      }
    };
    return (
      <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs">
        Apagar?
        <button type="button" onClick={apagar} disabled={apagando} className="font-semibold text-theme-danger hover:underline">
          Sim
        </button>
        <button type="button" onClick={() => setConfirmando(false)} className="text-content-secondary hover:underline">
          Não
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => onEditar(a)}
        aria-label={`Editar apontamento de ${fmtData(a.data)}`}
        className="rounded p-1 text-content-secondary hover:bg-surface-hover hover:text-content-primary"
      >
        <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">edit</span>
      </button>
      <button
        type="button"
        onClick={() => setConfirmando(true)}
        aria-label={`Apagar apontamento de ${fmtData(a.data)}`}
        className="rounded p-1 text-content-secondary hover:bg-surface-hover hover:text-theme-danger"
      >
        <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">delete</span>
      </button>
    </span>
  );
};

const Sku: FC<{ a: ApontamentoReuniao }> = ({ a }) =>
  a.sku_codigo ? (
    <>
      <span className="font-mono">{a.sku_codigo}</span>
      {a.sku_descricao && <span className="block text-content-muted">{a.sku_descricao}</span>}
    </>
  ) : (
    <span className="text-content-muted">—</span>
  );

const Linha: FC<{ a: ApontamentoReuniao; acoes?: AcoesLinha }> = ({ a, acoes }) => (
  <tr className="border-t border-theme-border align-top break-inside-avoid" data-testid={`apontamento-${a.id}`}>
    <td className="whitespace-nowrap px-2 py-2 print:px-1">{fmtData(a.data)}</td>
    <td className="whitespace-nowrap px-2 py-2 print:px-1">{a.maquina || <span className="text-content-muted">Fábrica toda</span>}</td>
    <td className="px-2 py-2 print:px-1 text-xs">
      <Sku a={a} />
    </td>
    <td className="px-2 py-2 print:px-1">{a.turno ? a.turno_label : <span className="text-content-muted">—</span>}</td>
    <td className="px-2 py-2 print:px-1">{a.motivo_label}</td>
    <td className="px-2 py-2 print:px-1 text-right tabular-nums">{a.quantidade === null ? '—' : fmtUn(a.quantidade)}</td>
    <td className="min-w-[12rem] whitespace-pre-line px-2 py-2 print:px-1 text-content-secondary print:min-w-[10rem]">{a.observacao || '—'}</td>
    <td className="px-2 py-2 print:px-1 text-xs text-content-muted" title={`Registrado em ${fmtDataHora(a.created_at)}`}>
      {a.autor.nome}
    </td>
    {acoes && (
      <td className="px-2 py-2 print:px-1 text-right print-hide">
        <Acoes a={a} {...acoes} />
      </td>
    )}
  </tr>
);

/** Tabela dos apontamentos: a mesma na tela de registro, na Visão geral e no PDF. */
const ApontamentosLista: FC<Props> = ({ apontamentos, onEditar, onApagar, testId }) => {
  const acoes = onEditar && onApagar ? { onEditar, onApagar } : undefined;
  // `relative`: o `sr-only` do cabeçalho de ações não escapa da rolagem e alarga a página.
  // No papel não há rolagem: a tabela encolhe a letra e quebra a observação.
  return (
    <div className="relative overflow-x-auto print:overflow-visible" data-testid={testId}>
      <table className="w-full text-sm text-content-primary print:text-xs">
        <thead className="text-xs uppercase tracking-wide text-content-muted">
          <tr>
            <th className={TH}>Data</th>
            <th className={TH}>Máquina</th>
            <th className={TH}>SKU</th>
            <th className={TH}>Turno</th>
            <th className={TH}>Motivo</th>
            <th className={`${TH} text-right`}>Qtd. afetada</th>
            <th className={TH}>Observação</th>
            <th className={TH}>Registrado por</th>
            {acoes && <th className={`${TH} print-hide`}><span className="sr-only">Ações</span></th>}
          </tr>
        </thead>
        <tbody>
          {apontamentos.map((a) => (
            <Linha key={a.id} a={a} acoes={acoes} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ApontamentosLista;
